import React, { useState, useEffect } from "react";
import {
  Text,
  StyleSheet,
  View,
  Platform,
  TouchableOpacity,
} from "react-native";
import moment from "moment";

import Utils, { navigate, hasItems } from "../../../../../helpers/utils";
import appPages from "../../../../../appPages";
import appStyles from "../../../../appStyles";
import AppTheme from "../../../AppTheme";
import cardStyles from "../cardStyles";
import CloseIcon from "../../../common/dialog/CloseIcon";
import ElementIcon from "../../../elements/list/ElementIcon";
import Icons from "../../../common/icons";
import JournalHelper from "../../../journal/JournalHelper";
import ManaButton from "../../../common/ManaButton";
import ManaStopwatch from "../../../common/ManaStopwatch";
import SettingsHelper from "../../../more/SettingsHelper";
import TimePickerSwipeHeader from "../../../common/timepicker/TimePickerSwipeHeader";

const DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";
const TIME_FORMAT = "hh:mma";
const INCREMENT = 1; // seconds

export default function TimerCard({ controller, data = {} }) {
  const [showCard, setShowCard] = useState(true);
  const [timeslot, setTimeslot] = useState(null);
  const [element, setElement] = useState(null);
  const [time, setTime] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [startTime, setStartTime] = useState(null);
  let interval = null;

  // ------------------------------------- METHODS -------------------------------------
  const _getCurrentTimeslot = (allTimeslots) => {
    const now = moment();

    return allTimeslots.find((t) => {
      const timeslotStartTime = moment(t.time);
      const timeslotEndTime = timeslotStartTime
        .clone()
        .add(t.duration, "minutes");

      return now.isBetween(timeslotStartTime, timeslotEndTime, "minutes", "[]");
    });
  };

  const _initData = () => {
    // From Element picker page
    const storageElement = Utils.LOCAL_CACHE["timer_element"];

    // Find current timeslot
    const t = _getCurrentTimeslot(controller.state.timeslots);
    if (!t) return;

    const el = hasItems(t.predicted_activities)
      ? t.predicted_activities[0]
      : t.activity;

    setTimeslot(t);

    // Keep initial element for stopped state
    if (!element || storageElement) setElement(storageElement || el);
    delete Utils.LOCAL_CACHE["timer_element"];
  };

  useEffect(() => {
    if (controller.state.timeslots) {
      const settings = controller.state.settings;
      setShowCard(!!settings.show_timer_card);

      _initData();
    }
  }, [Utils.LOCAL_CACHE["timer_element"], controller.state.timeslots]);

  // Stopwatch
  useEffect(() => {
    if (isActive && isPaused === false) {
      interval = setInterval(() => {
        setTime((t) => t + INCREMENT);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => {
      clearInterval(interval);
    };
  }, [isActive, isPaused]);

  const togglePlayState = () => {
    if (isActive) {
      _stopTimer();
    } else {
      _startTimer();
    }
  };

  const _startTimer = () => {
    const start = moment();
    setIsActive(true);
    setStartTime(start);

    // Call timer API
    const action = {
      action: "save",
      target: "timer",
      operation: "start",
      time: start.format(DATE_FORMAT),
      id_element: element.id,
      timeslot,
    };

    console.log("Timer started");
    controller.cardAction(action);
  };

  const _stopTimer = () => {
    const endDate = startTime.clone().add(time, "seconds");

    const newTs = {
      ...timeslot,
      activity: element,
      time: startTime.format(DATE_FORMAT),
      oldEndTime: timeslot.endTime,
      oldStartTime: timeslot.time,
      startTime: startTime.format(TIME_FORMAT),
      endDate: endDate.format(DATE_FORMAT),
      endTime: endDate.format(TIME_FORMAT),
      source: "edit",
      user_confirmed: true,
      was_predicted: 1,
      was_first_option: 1,
      reload: true,
    };

    setTimeslot(newTs);
    _confirmTimeslot({ timeslot: newTs });
  };

  const _confirmTimeslot = (ts) => {
    setIsActive(false);
    setIsPaused(true);

    JournalHelper.saveTimeslotToDB(controller, controller.state.timeslots, ts);
  };

  const undo = () => {
    _confirmTimeslot({
      timeslot: {
        ...timeslot,
        activity: { ...element, id: "-" },
      },
      is_confirmed: true,
      was_predicted: 0,
      was_first_option: 0,
      reload: true,
      sourceOverride: "undo",
    });

    initNewTimer();
  };

  const initNewTimer = () => {
    setIsActive(false);
    setIsPaused(false);
    setTime(null);
    setStartTime(null);
    _initData();
  };

  const closeCard = () => {
    if (isActive) {
      _stopTimer();
      setIsActive(false);
      setIsPaused(false);
      setStartTime(null);
      setTime(null);
    }

    setShowCard(false);
    SettingsHelper.toggleTimerCardPreferences(controller, false);
  };

  const onElementPressed = () => {
    navigate(controller, appPages.timerElementsPage);
  };
  // ------------------------------------- END METHODS -------------------------------------
  const bgStyle =
    element?.is_major === 1 || element?.is_core === 1
      ? {
          backgroundColor: "#f3f2f1",
          borderRadius: 100,
        }
      : {};

  return !timeslot || !element || !showCard ? null : (
    <View style={cardStyles.container}>
      <View style={[cardStyles.card, { minHeight: 98 }]}>
        <CloseIcon onPress={closeCard} style={cardStyles.closeButton} />

        {!isPaused ? (
          <View style={styles.content}>
            {/* Play Button */}
            <View style={styles.playContainer}>
              <Icons.PlayStopIcon
                isActive={isActive}
                onPress={togglePlayState}
              />
            </View>

            <View style={styles.rightContent}>
              {/* Selected Element */}
              <View style={[styles.elementContainer]}>
                <TouchableOpacity
                  style={[styles.element, bgStyle]}
                  onPress={onElementPressed}
                >
                  <ElementIcon
                    style={styles.icon}
                    element={element}
                    onPress={onElementPressed}
                  />
                  <Text style={styles.elementName} numberOfLines={1}>
                    {element.name}
                  </Text>
                </TouchableOpacity>
              </View>

              <View style={styles.timerInfo}>
                {startTime ? (
                  <View>
                    <Text style={styles.startedLabel}>Started</Text>
                    <Text style={styles.started}>
                      {startTime.format("H:mma")}
                    </Text>
                  </View>
                ) : null}

                <View style={{ marginLeft: "auto" }}>
                  <ManaStopwatch seconds={time} />
                </View>
              </View>
            </View>
          </View>
        ) : (
          <View>
            <View style={styles.row}>
              <Icons.HourGlassIcon color={AppTheme.DEFAULT_COLOR} />

              <TimePickerSwipeHeader
                style={styles.timeheader}
                timeslot={{
                  time: startTime.format(DATE_FORMAT),
                  startTime: startTime.format(TIME_FORMAT),
                  endTime: startTime
                    .clone()
                    .add(time, "seconds")
                    .format(TIME_FORMAT),
                }}
              />
            </View>

            {/* Selected Element */}
            <View style={[styles.elementContainer]}>
              <View style={[styles.element, bgStyle]}>
                <ElementIcon style={styles.icon} element={element} />
                <Text style={styles.elementName} numberOfLines={1}>
                  {element.name}
                </Text>
              </View>
            </View>

            <View style={[appStyles.horizontalButtons, { marginTop: 5 }]}>
              <ManaButton
                onPress={undo}
                text="Undo"
                style={[styles.button, { marginRight: 6 }]}
                styleText={styles.buttonText}
              />

              <ManaButton
                isMainButton
                onPress={initNewTimer}
                text="New timer"
                style={styles.button}
                styleText={styles.buttonText}
              />
            </View>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    width: "100%",
    flexDirection: "row",
    height: 98,
  },
  row: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
  },
  playContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
    height: "100%",
  },
  rightContent: {
    flex: 1,
    paddingHorizontal: 10,
  },
  elementContainer: {
    borderBottomColor: AppTheme.borderColor,
    borderBottomWidth: 1,
  },
  element: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 5,
    height: 40,
  },
  icon: {
    width: 24,
    height: 24,
    marginRight: 6,
  },
  elementName: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
  },
  timeheader: {
    paddingTop: 0,
    paddingLeft: 6,
    paddingBottom: 5,
  },
  button: {
    paddingHorizontal: 10,
    borderRadius: 6,
    width: "50%",
    height: 32,
  },
  buttonText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.175,
  },

  timerInfo: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    flexDirection: "row",
    flex: 1,
  },
  started: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
  },
  startedLabel: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "300",
    fontSize: 10,
    lineHeight: 12,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
  },
});
